import type React from 'react';
import { useCallback, useState } from 'react';

import enLocale from '@fullcalendar/core/locales/en-gb';
import frLocale from '@fullcalendar/core/locales/fr';
import itLocale from '@fullcalendar/core/locales/it';
import dayGridPlugin from '@fullcalendar/daygrid';
import interactionPlugin from '@fullcalendar/interaction';
import rrulePlugin from '@fullcalendar/rrule';
import timeGridPlugin from '@fullcalendar/timegrid';
import { Box, useTheme } from '@mui/material';
import { OrdList, useCanEdit } from '@ragu-components/ord-components';
import { Resources } from '@ragu-components/ord-resources';
import { Calendar as RaCalendar, getFilterValuesFromInterval } from '@ratto-components/ra-calendar';
import { useLocaleState, useCreatePath, useResourceContext, useCanAccess, useTranslate } from 'react-admin';
import { useNavigate } from 'react-router-dom';

import { getColor } from 'src/lib/colorUtils';

import { CalendarsList } from '../CalendarsList';

import { CalendarEventsForm } from './CalendarEventsForm';
import { dateConverterFromServerToFC, dateConverterFromInputToFC } from './utils';

import type { ResourceType } from '../../types';
import type { DateSelectArg, EventInput, EventClickArg } from '@fullcalendar/core';
import type { OrdListProps } from '@ragu-components/ord-components';
import type { Calendar, MultiCalendarEvent, MultiPersonalCalendarEvent } from '@ragu-components/ord-types';
import type { Identifier } from 'react-admin';

type CalendarEventsListProps = Partial<OrdListProps> & ResourceType;

export const CalendarEventsList = ({
  isPersonalResource = true,
  ...props
}: CalendarEventsListProps): React.ReactElement => {
  const theme = useTheme();
  const translate = useTranslate();
  const [selectedCalendarIds, setSelectedCalendarIds] = useState<Identifier[]>([]);
  const [selectedEventCalendarId, setSelectedEventCalendarIds] = useState<string | undefined>(undefined);
  const [showingEventId, setShowingEventId] = useState<Identifier | undefined>(undefined);
  const [calendars, setCalendars] = useState<Calendar[]>([]);
  const [createOpen, setCreateOpen] = useState(false);
  const [locale] = useLocaleState();
  const navigate = useNavigate();
  const createPath = useCreatePath();
  const resource = useResourceContext();
  const { canAccess: canCreateAccess } = useCanAccess({ action: 'create', resource });
  const { canAccess: canEditAccess } = useCanAccess({ action: 'edit', resource });
  const { canAccess: canShowAccess } = useCanAccess({ action: 'show', resource });
  const canCreate = canCreateAccess === true;
  const canEdit = canEditAccess === true;
  const canShow = canShowAccess === true;
  const canEditRecord = useCanEdit();

  const queryOptions = { meta: { calendar_ids: selectedCalendarIds } };

  const calendarResource = isPersonalResource ? `me/${Resources.calendars}` : Resources.calendars;

  const converter = (event: MultiPersonalCalendarEvent | MultiCalendarEvent): EventInput => ({
    id: String(event.id),
    title: event.title,
    start: dateConverterFromInputToFC(event.dtstart, event.tzid, event.all_day),
    end:
      event.all_day === true
        ? dateConverterFromServerToFC(event.dtend, 'exclusive')
        : dateConverterFromInputToFC(event.dtend, event.tzid, event.all_day),
    backgroundColor: getColor(event.calendar?.id ?? ''),
    borderColor: getColor(event.calendar?.id ?? ''),
    extendedProps: { calendarId: event.calendar?.id },
    allDay: event.all_day,
    // This is for dragging feature, until we know how to change the payload and path of the requests that fullcalendars makes automatically
    editable: false,
    // recurrence fields mapping
    rrule:
      event.rrule !== null
        ? {
            freq: event.rrule?.freq,
            interval: event.rrule?.interval,
            byweekday: event.rrule?.byweekday?.length === 0 ? null : event.rrule?.byweekday,
            dtstart: dateConverterFromInputToFC(event.dtstart, event.tzid, event.all_day, true),
            tzid: event.tzid,
            until: event.rrule?.until,
            bymonthday: event.rrule?.bymonthday?.length === 0 ? null : event.rrule?.bymonthday,
            bymonth: event.rrule?.bymonth?.length === 0 ? null : event.rrule?.bymonth,
            count: event.rrule?.count,
          }
        : undefined,
  });

  const onSelectedIds = useCallback((selectedIds: Identifier[]) => {
    setSelectedCalendarIds(selectedIds);
  }, []);

  const onCalendars = useCallback((records: Calendar[]) => {
    setCalendars(records);
  }, []);

  const handleEventClick = useCallback(
    (clickInfo: EventClickArg): void => {
      const calendarId = clickInfo.event.extendedProps.calendarId as string | undefined;
      setSelectedEventCalendarIds(calendarId);
      // Events hold no labels of their own — their label scope is the parent calendar's, so that
      // record carries the check. Without it the recordless evaluation matches any edit label the
      // user holds on any resource, which would open the editable form to a readonly user
      const parentCalendar = calendars.find((calendar) => String(calendar.id) === calendarId);
      if (canEdit && canEditRecord(resource ?? '', parentCalendar)) {
        void navigate(createPath({ resource, id: clickInfo.event.id, type: 'edit' }));
      } else if (canShow) {
        setShowingEventId(clickInfo.event.id);
      }
    },
    [calendars, canEdit, canEditRecord, canShow, createPath, navigate, resource]
  );

  const handleDateSelect = useCallback((selectInfo: DateSelectArg): void => {
    // The library's default select handler navigates to `/${resource}/create`, which has no
    // registered route here (creation is dialog-based) and just leaves the UI stuck on that URL
    selectInfo.view.calendar.unselect();
    setCreateOpen(true);
  }, []);

  const calendarsList = (
    <CalendarsList
      resource={calendarResource}
      isPersonalResource={isPersonalResource}
      onSelectedIds={onSelectedIds}
      onCalendars={onCalendars}
    />
  );

  return (
    <Box sx={{ display: 'flex', flexDirection: 'row', width: '100%', height: '100%' }}>
      <OrdList
        actions={false}
        queryOptions={queryOptions}
        icon={<></>}
        filterDefaultValues={getFilterValuesFromInterval()}
        pagination={false}
        perPage={0}
        sy={{
          '& .RaList-content': {
            borderRadius: 1,
          },
          height: '100%',
          width: '100%',
          pr: theme.spacing(1),
          '& .RaList-noActions': {
            mt: 0,
          },
        }}
        {...props}
      >
        <RaCalendar
          convertToEvent={converter}
          plugins={[dayGridPlugin, timeGridPlugin, interactionPlugin, rrulePlugin]}
          handleWindowResize
          height='100%'
          locales={[enLocale, frLocale, itLocale]}
          locale={locale}
          eventClick={handleEventClick}
          // Disable day-cell selection entirely when the user can't create events; when they
          // can, open the create dialog instead of the library's default navigate-to-/create
          selectable={canCreate}
          select={handleDateSelect}
          headerToolbar={{
            left: canCreate ? 'createEvent prev,next' : 'prev,next',
            center: 'title',
            right: 'today dayGridMonth,timeGridWeek,timeGridDay',
          }}
          customButtons={{
            createEvent: {
              text: translate(`resources.${resource}.operations.create`, { _: 'Create Event' }),
              click: () => {
                setCreateOpen(true);
              },
            },
          }}
          // This is for dragging feature, until we know how to change the payload and path of the requests that fullcalendars makes automatically
          editable={false}
          sx={{
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            '& .fc-view-harness': {
              flex: 1,
              minHeight: 0,
            },
            '& .fc-button': {
              borderRadius: theme.shape.borderRadius,
              px: 2,
              textTransform: 'capitalize',
              ...theme.typography.subtitle2,
            },
            '& .fc-button-group': {
              '& > .fc-button': {
                borderRadius: '0',
              },
              '& > .fc-button:first-of-type': {
                borderTopLeftRadius: theme.shape.borderRadius,
                borderBottomLeftRadius: theme.shape.borderRadius,
              },
              '& > .fc-button:last-of-type': {
                borderTopRightRadius: theme.shape.borderRadius,
                borderBottomRightRadius: theme.shape.borderRadius,
              },
            },
            '& .fc-button.fc-createEvent-button': {
              backgroundColor: theme.palette.primary.main,
              borderColor: theme.palette.primary.main,
              color: theme.palette.primary.contrastText,
              '&:hover': {
                backgroundColor: theme.palette.primary.main,
                borderColor: theme.palette.primary.main,
                color: theme.palette.primary.contrastText,
              },
            },
            '& .fc-today-button': {
              borderRadius: '999px',
            },
            '& .fc-button-primary:not(:disabled).fc-button-active': {
              backgroundColor: theme.palette.primary.outlinedBorder,
            },
            '& .fc-button-primary:disabled': {
              backgroundColor: theme.palette.primary.outlinedBorder,
            },
            '& .fc-scrollgrid': {
              borderRadius: theme.shape.borderRadius,
              overflow: 'hidden',
              borderBottomRightRadius: theme.shape.borderRadius,
            },
            '& .fc-scrollgrid-section:last-of-type td': {
              borderBottomRightRadius: theme.shape.borderRadius,
              borderBottomLeftRadius: theme.shape.borderRadius,
            },
            '& .fc-daygrid-body': {
              borderBottomRightRadius: theme.shape.borderRadius,
            },
            '& .fc .fc-daygrid-day.fc-day-today': {
              background: theme.palette.primary.focusVisible,
              borderRadius: 0,
            },
          }}
        />
      </OrdList>
      {/* Controlled dialogs mount their create/edit controller even while closed, and react-admin
          access control redirects straight to /access-denied on mount — render them only when allowed */}
      {canCreate && (
        <CalendarEventsForm
          type='create'
          isPersonalResource={isPersonalResource}
          selectedCalendarIds={selectedCalendarIds}
          isOpen={createOpen}
          open={() => {
            setCreateOpen(true);
          }}
          close={() => {
            setCreateOpen(false);
          }}
        />
      )}
      {canEdit && (
        <CalendarEventsForm
          type='edit'
          isPersonalResource={isPersonalResource}
          selectedCalendarIds={selectedCalendarIds}
          parentCalendarId={selectedEventCalendarId}
        />
      )}
      {canShow && showingEventId !== undefined && (
        <CalendarEventsForm
          type='edit'
          isReadonly
          showId={showingEventId}
          isPersonalResource={isPersonalResource}
          selectedCalendarIds={selectedCalendarIds}
          parentCalendarId={selectedEventCalendarId}
          close={() => {
            setShowingEventId(undefined);
          }}
        />
      )}
      {/* OrdList defaults to flex: 1 1 0%, so this needs a fixed basis to stay narrow */}
      <Box sx={{ flex: '0 0 340px', minWidth: 0, height: '100%' }}>{calendarsList}</Box>
    </Box>
  );
};

export default CalendarEventsList;
